import type {
  QuestionnaireRequestAsset,
  RequestAssetBucket,
  RequestAssetFilePart,
} from '@retracedgmbh/modules/questionnaires/contracts';
import { API_ROOT_URL, BACKEND_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

export interface DocumentPrivacyStatus {
  status:
    | 'public'
    | 'private'
    | 'private_requests'
    | 'shared'
    | 'shared_to'
    | 'shared_by';
  requests: number;
  approvedRequests: number;
  company?: { name: string } | null;
}

export interface DocumentPermission {
  status: 'requested' | 'none';
  data: null | { documentId: string };
}

export interface Document {
  id: string;
  name: string;
  fileUrl: string | null;
  type: string | null;
  isPublic: boolean;
  updatedAt: string | null;
  updatedByUserName: string | null;
  updatedByCompanyName: string | null;
  privacyStatus: DocumentPrivacyStatus | null;
  permission?: DocumentPermission | null;
  // Present when isWithNetworkDocuments=true
  companyId?: string | null;
  companyName?: string | null;
  internalCompanyCode?: string | null;
  notes?: string | null;
  requestCompanyFacilityProcesses?: string | null;
}

export interface NetworkDocument extends Document {
  companyId: string;
  companyName: string | null;
  requestCompanyFacilityProcesses: string | null;
}

export interface DocumentsResponse {
  data: Document[];
  meta: { totalCount: number; pageSize: number; page: number };
}

export interface NetworkDocumentsResponse {
  data: NetworkDocument[];
  meta: { totalCount: number; pageSize: number; page: number };
}

/** One of the caller's own documents in its archive, with who archived it. */
export interface ArchivedDocument extends Document {
  archivedAt: string | null;
  archivedByUserName: string | null;
  archivedByCompanyName: string | null;
}

export interface ArchivedDocumentsResponse {
  data: ArchivedDocument[];
  meta: { totalCount: number; pageSize: number; page: number };
}

export interface DocumentType {
  id: string;
  title: string;
}

export type DocumentTypesResponse = DocumentType[];

export interface DocumentPermissionEntry {
  sharedWithCompanyId: string;
  sharedWithCompanyName: string | null;
  requestedAt: string | null;
  approvedAt: string | null;
}

export interface DocumentPermissionsResponse {
  id: string;
  name: string;
  permissions: DocumentPermissionEntry[];
}

export interface BulkCreateDocumentItem {
  name: string;
  documentTypeId: string;
  fileId: string;
  isPublic: boolean;
}

export interface SharedCompanyDocument {
  id: string;
  name: string;
  fileId?: string | null;
  fileUrl?: string | null;
  createdAt?: string | null;
}

// Pure RPC — takes a pre-built query string, returns raw response

export async function fetchSharedCompanyDocuments(
  companyId: string,
  documentType: string,
): Promise<{ data: SharedCompanyDocument[] }> {
  const qs = new URLSearchParams({ companyId, documentType }).toString();
  return apiFetch<{ data: SharedCompanyDocument[] }>(
    `/api/company-documents/shared?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function fetchDocuments(qs: string): Promise<DocumentsResponse> {
  return apiFetch<DocumentsResponse>(
    `/api/company-documents?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function fetchNetworkDocuments(
  qs: string,
): Promise<NetworkDocumentsResponse> {
  return apiFetch<NetworkDocumentsResponse>(
    `/api/company-documents?${qs}`,
    {},
    API_ROOT_URL,
  );
}

// Thin wrapper over the canonical `/document-types` list below, for the agentic
// flows that only need the rows. Deprecated types are excluded server-side.
export async function fetchDocumentTypes(
  qs: string,
): Promise<DocumentTypesResponse> {
  const res = await fetchDocumentTypesList(qs);
  return res.data;
}

// Canonical `/document-types` list route.

export interface DocumentTypeListItem {
  id: string;
  title: string;
  documentClass: 'TRANSACTIONAL' | 'COMPANY' | 'PRODUCT';
  isDeprecated: boolean;
  impactAreaId: string | null;
}

export interface DocumentTypesListResponse {
  metadata: {
    success: boolean;
    // `page` is 1-based here, while the `page` query param is 0-based.
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
  data: DocumentTypeListItem[];
}

export async function fetchDocumentTypesList(
  qs: string,
): Promise<DocumentTypesListResponse> {
  return apiFetch<DocumentTypesListResponse>(
    `/document-types?${qs}`,
    {},
    BACKEND_URL,
  );
}

export async function fetchDocumentPermissions(
  id: string,
): Promise<DocumentPermissionsResponse> {
  return apiFetch<DocumentPermissionsResponse>(
    `/api/company-documents/${id}`,
    {},
    API_ROOT_URL,
  );
}

export async function patchDocument(
  id: string,
  body: { name?: string; documentTypeId?: string },
): Promise<void> {
  await apiFetch<void>(
    `/api/company-documents/${id}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

export async function bulkArchiveDocuments(ids: string[]): Promise<void> {
  await apiFetch<void>(
    '/api/company-documents/bulk-archive',
    { method: 'PATCH', body: JSON.stringify(ids) },
    API_ROOT_URL,
  );
}

export async function fetchArchivedDocuments(
  qs: string,
): Promise<ArchivedDocumentsResponse> {
  return apiFetch<ArchivedDocumentsResponse>(
    `/api/company-documents/archived?${qs}`,
    {},
    API_ROOT_URL,
  );
}

/** Takes the caller's own documents back out of its archive. Other ids are skipped. */
export async function bulkRestoreDocuments(ids: string[]): Promise<void> {
  await apiFetch<void>(
    '/api/company-documents/bulk-restore',
    { method: 'PATCH', body: JSON.stringify(ids) },
    API_ROOT_URL,
  );
}

export async function bulkCreateDocuments(payload: {
  companyId: string;
  documents: BulkCreateDocumentItem[];
}): Promise<Array<{ id: string; fileId?: string | null; name?: string }>> {
  // Backend returns the created company_documents rows as a bare array.
  const res = (await apiFetch<unknown>(
    '/api/company-documents/bulk-create',
    { method: 'POST', body: JSON.stringify(payload) },
    API_ROOT_URL,
  )) as
    | Array<{ id: string; fileId?: string | null; name?: string }>
    | { data: Array<{ id: string; fileId?: string | null; name?: string }> }
    | undefined;
  if (Array.isArray(res)) return res;
  return res?.data ?? [];
}

/**
 * Creates documents as the answer to a questionnaire DOCUMENTS question.
 *
 * Carries **no company id**: the server files the documents on the company the
 * answer request is about and shares them back to the brand that asked.
 * `bulkCreateDocuments` takes the target company in its body and then requires it
 * to be in the caller's own network, which an answering party answering on
 * another company's behalf never is — so this page used to send its *own*
 * company, quietly filing the supplier's documents on the wrong profile
 * (DELTA-2415).
 */
export async function createQuestionnaireRequestDocuments(
  requestId: string,
  payload: {
    documents: Array<{
      name: string;
      documentTypeId: string;
      fileId: string;
    }>;
  },
): Promise<{ data: { documents: Array<{ id: string }> } }> {
  return apiFetch<{ data: { documents: Array<{ id: string }> } }>(
    `/api/questionnaires/requests/${requestId}/documents`,
    { method: 'POST', body: JSON.stringify(payload) },
    API_ROOT_URL,
  );
}

/**
 * The assets held by the company an answer request is *about*, plus the per-type
 * detail the answer form renders.
 *
 * Replaces the three per-type list calls the pickers used to make. Those reached
 * certificates through the caller's own network and audits through per-asset
 * sharing rows, so an answering party answering on another company's behalf saw
 * empty pickers (DELTA-2415).
 */
export async function fetchQuestionnaireRequestAssets(
  requestId: string,
  bucket: RequestAssetBucket,
): Promise<{ data: { assets: QuestionnaireRequestAsset[] } }> {
  return apiFetch<{ data: { assets: QuestionnaireRequestAsset[] } }>(
    `/api/questionnaires/requests/${requestId}/assets?bucket=${bucket}`,
    {},
    API_ROOT_URL,
  );
}

/**
 * A time-limited download URL for one asset's file, resolved through the request.
 *
 * Takes the **asset** id: the object key is resolved server-side from an asset
 * the request is allowed to reach, so unlike the removed `GET /v3/files/:fileUrl`
 * this cannot be pointed at another company's file. `part` picks between an
 * audit's report and its corrective action plan.
 */
export async function fetchQuestionnaireRequestAssetFile(
  requestId: string,
  assetId: string,
  bucket: RequestAssetBucket,
  part?: RequestAssetFilePart,
): Promise<{ data: { signedUrl: string } }> {
  const partQuery = part ? `&part=${part}` : '';
  return apiFetch<{ data: { signedUrl: string } }>(
    `/api/questionnaires/requests/${requestId}/assets/${assetId}/file?bucket=${bucket}${partQuery}`,
    {},
    API_ROOT_URL,
  );
}

export async function downloadDocuments(
  ids: string[],
): Promise<{ data: { id: string; url: string } }> {
  return apiFetch<{ data: { id: string; url: string } }>(
    '/company-documents/download',
    { method: 'POST', body: JSON.stringify(ids) },
    BACKEND_URL,
  );
}

export async function shareDocument(
  id: string,
  companyIds: string[],
): Promise<void> {
  await apiFetch<void>(
    `/api/company-documents/${id}/share`,
    { method: 'PUT', body: JSON.stringify(companyIds) },
    API_ROOT_URL,
  );
}

export async function makeDocumentPublic(id: string): Promise<void> {
  await apiFetch<void>(
    '/api/company-documents/make-public',
    { method: 'PATCH', body: JSON.stringify({ id, confirmation: 'public' }) },
    API_ROOT_URL,
  );
}

export async function requestDocumentAccess(id: string): Promise<void> {
  await apiFetch<void>(
    `/api/company-documents/${id}/request`,
    { method: 'POST', body: JSON.stringify({}) },
    API_ROOT_URL,
  );
}

export async function cancelDocumentAccessRequest(id: string): Promise<void> {
  await apiFetch<void>(
    `/api/company-documents/${id}/cancel-request`,
    { method: 'POST', body: JSON.stringify({}) },
    API_ROOT_URL,
  );
}
